<template>
  <v-container class="main" fluid>
    <SnackBarQueue v-model="messages"></SnackBarQueue>
    <!-- Model Version Info DIALOG -->
    <v-dialog v-if="dialog" v-model="dialog" transition="dialog-bottom-transition" max-width="600px">
      <v-card>
        <v-card-title class="text-center"> Content Model Version {{ selection.modelVersion.version }} </v-card-title>
        <v-card-subtitle class="mt-1">
          released <strong>{{ new Date(selection.modelVersion.releaseDate).toLocaleString("de-AT") }}</strong>
          <br />
          by <strong>{{ selection.modelVersion.releasedBy }}</strong>
          <br />
          based on environment <strong>{{ selection.modelVersion.envName }}</strong>
        </v-card-subtitle>
        <v-card-text>
          <h1 class="text-h4">Configuration</h1>
          <br />
          <h2 class="text-h6">Excluded Fields</h2>
          <div v-html="excludedContentFields"></div>
          <h2 class="text-h6">Selected ContentTypes</h2>
          <div v-html="selectedContentTypes"></div>
          <h2 class="text-h6">Excluded ContentTypes</h2>
          <div v-html="excludedContentTypes"></div>
          <h2 class="text-h6">Excluded Fields for Specific ContentTypes</h2>
          <div v-html="selectedContentTypeForFields"></div>
          <br />
          <h1 class="text-h4">Release Notes</h1>
          <br />
          <div v-html="releaseNotes"></div>
        </v-card-text>
        <v-divider class="mx-4"></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="dialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card outlined flat class="pa-10">
      <h2 style="display: inline">Version Upgrade</h2>
      <RefreshSpaces style="float:right"></RefreshSpaces>
      <v-divider class="horizontal-divider" /><br />
      <v-alert color="indigo" dark>
        <v-icon>mdi-information</v-icon>
        Upgrade the content model of the selected target environment to the selected release version. The upgrade is done stepwise, where every version between
        the currently installed version in the target environment and the selected release version consitutes one step.
        <br />
      </v-alert>
      <!-- select Spaces -->
      <strong>Target Space</strong>
      <v-row class="ma-2">
        <SpaceEnvSelection
          class="mt-4"
          @changedSelection="
            (v) => {
              selection.targets = v;
            }
          "
          :spaces="spaces"
          :multiple="true"
        ></SpaceEnvSelection>
      </v-row>
      <strong>Currently Installed Versions</strong>
      <v-row class="ma-2 yellow lighten-5">
        <div v-html="targetVersionInfos"></div>
      </v-row>
      <strong>Content Model Versions</strong>
      <v-row class="ma-2">
        <v-select
          style="width:50%"
          outlined
          :items="modelVersions"
          item-text="version"
          return-object
          v-model="selection.modelVersion"
          :loading="loading.contentModelVersions"
        />
        <div style="width:50%" class="pl-2">
          <v-btn class="secondary-button" v-if="selection.modelVersion" @click="($event) => (dialog = true)">Version Info</v-btn>
        </div>
      </v-row>
      <v-checkbox class="ma-2" v-model="backupTargetSpace" :label="`Make a backup of the target spaces' content model`"></v-checkbox>
      <v-row>
        <v-btn
          class="primary-button"
          dark
          @click="upgradeModel()"
          :loading="loading.upgrading"
          :disabled="!checkVersions() || existsJobEnv || loading.upgrading"
          >Upgrade Now</v-btn
        >
        <v-spacer />
        <v-btn class="secondary-button" dark v-if="jobId && !loading.upgrading" @click="getJob(jobId, null)" :loading="loading.upgrading"
          >Get Job Progress</v-btn
        >
        <v-spacer />
        <v-btn class="red-button" dark v-if="jobId" @click="cancelJob(jobId)" :loading="loading.cancelling">Cancel Job</v-btn>
      </v-row>
      <div class="text-center" v-if="loading.upgrading">
        <v-progress-circular :rotate="360" :size="100" :width="15" :value="jobProgress" color="teal"> {{ jobProgress }}% </v-progress-circular>
      </div>
    </v-card>
    <JobLogs :jobLogs="jobMessages"></JobLogs>
    <br />
    <VueJsonViewer :value="result" :expand-depth="5" boxed v-if="result"></VueJsonViewer>
  </v-container>
</template>

<script>
import SnackBarQueue from "../helpers/SnackBarQueue";
import semver from "semver";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import VueJsonViewer from "vue-json-viewer";
import RefreshSpaces from "./helpers/RefreshSpaces.vue";
import { mapState } from "vuex";
import SpaceEnvSelection from "./helpers/SpaceEnvSelection.vue";
import JobLogs from "./helpers/JobLogs.vue";
import Vue from "vue";

export default {
  data() {
    return {
      messages: [],
      modelVersions: [],
      loading: {
        contentModelVersion: false,
        targetModelVersion: false,
        upgrading: false,
        cancelling: false,
      },
      targetModelVersions: {},
      pollJob: null,
      jobProgress: 0,
      jobMessages: null,
      existsJobEnv: false,
      jobId: null,
      selection: {
        targets: [],
        modelVersion: null,
      },
      backupTargetSpace: true,
      releaseNotes: "",
      dialog: false,
      result: null,
    };
  },
  components: {
    SnackBarQueue,
    VueJsonViewer,
    RefreshSpaces,
    SpaceEnvSelection,
    JobLogs,
  },
  watch: {
    "selection.targets": {
      handler() {
        for (const target of this.selection.targets) {
          this.getEnvModelVersion(target.space.id, target.environment.id);
        }
        this.existsJob();
      },
      deep: true,
    },
    "selection.modelVersion"() {
      if (this.selection.modelVersion) {
        this.releaseNotes = documentToHtmlString(this.selection.modelVersion.releaseNotes);
      }
    },
  },
  computed: {
    ...mapState({
      spaces: (state) => state.contentfulSpaces,
    }),
    excludedContentFields() {
      if (!this.selection.modelVersion?.options.excludedContentFields?.length) return "";
      return "<ul>" + this.selection.modelVersion.options.excludedContentFields.map((f) => "<li>" + f + "</li>").join("") + "</ul>";
    },
    selectedContentTypes() {
      if (!this.selection.modelVersion?.options.selectedContentTypes?.length) return "";
      return "<ul>" + this.selection.modelVersion.options.selectedContentTypes.map((f) => "<li>" + f.name + "</li>").join("") + "</ul>";
    },
    excludedContentTypes() {
      if (!this.selection.modelVersion?.options.excludedContentTypes?.length) return "";
      return "<ul>" + this.selection.modelVersion.options.excludedContentTypes.map((f) => "<li>" + f.name + "</li>").join("") + "</ul>";
    },
    selectedContentTypeForFields() {
      if (!this.selection.modelVersion?.options.selectedContentTypeForFields?.length) return "";
      return (
        "<ul>" +
        this.selection.modelVersion.options.selectedContentTypeForFields
          .filter((v) => v.contentType)
          .map((t) => "<li><strong>" + t.contentType.name + "</strong>: " + t.fields.map((f) => f.name).join(", ") + "</li>")
          .join("") +
        "</ul>"
      );
    },
    targetVersionInfos() {
      let str = "<ul>";
      str += this.selection.targets
        .map(
          (t) => "<li>" + t.space.name + " (" + t.environment.name + "): " + (this.targetModelVersions[t.space.id + "-" + t.environment.id] ?? "-") + "</li>"
        )
        .join(" ");
      str += "</ul>";
      return str;
    },
  },
  beforeDestroy() {
    clearInterval(this.pollJob);
  },
  methods: {
    checkVersions() {
      if (!this.selection.modelVersion) return false;
      return this.selection.targets.every((t) => {
        let targetVersion = this.targetModelVersions[t.space.id + "-" + t.environment.id];
        if (!targetVersion) return false;
        return semver.lte(targetVersion, this.selection.modelVersion.version);
      });
    },
    async getEnvModelVersion(spaceId, envId) {
      this.loading.targetModelVersion = true;
      this.axios
        .get(`/contentful/content-model-version-info?spaceId=${spaceId}&envId=${envId}`)
        .then((res) => {
          if (res.data.version) Vue.set(this.targetModelVersions, spaceId + "-" + envId, res.data.version);
          console.log(this.selection.targets);
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed loading version space entries! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        })
        .finally(() => {
          this.loading.targetModelVersion = false;
        });
    },
    async getModelVersions() {
      this.loading.contentModelVersions = true;
      this.axios
        .get(`/contentful/content-model-versions`)
        .then(async (res) => {
          if (res && res.data) {
            this.modelVersions = res.data;
          }
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed loading master space! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        })
        .finally(() => (this.loading.contentModelVersions = false));
    },
    async existsJob() {
      this.axios
        .get(`/contentful/exists-job?spaceId=${this.selection.targetSpace}&environmentId=${this.selection.targetEnv}`)
        .then(async (res) => {
          if (res) {
            this.existsJobEnv = typeof res.data != "string";
            if (this.existsJobEnv) {
              this.jobId = res.data.id;
              this.messages.push({
                message: `Job for this Environment already exists!`,
                color: "error",
              });
            } else {
              this.jobId = null;
            }
          }
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed loading master space! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        });
    },
    async getJob(jobId, res) {
      this.loading.upgrading = true;
      this.pollJob = setInterval(() => {
        this.axios
          .get(`/contentful/get-job/`, {
            params: {
              jobId: jobId,
            },
          })
          .then((res) => {
            if (res.data.progress === 100 && (res.data.returnvalue || res.data.failed) && this.result == null) {
              clearInterval(this.pollJob);
              this.loading.upgrading = false;
              this.jobProgress = 0;
              if (res.data.logs) {
                this.jobMessages = res.data.logs;
              }
              if (res.data.failed) {
                this.messages.push({
                  message: `Failed upgrading! ${res.data.message}`,
                  color: "error",
                });
                this.result = res.data;
              } else {
                this.messages.push({ message: "Upgrading successful!", color: "success" });
              }
              this.result = res.data.result;
            } else {
              if (res.data.logs) {
                this.jobMessages = res.data.logs;
              }
              this.jobProgress = res.data.progress;
            }
          })
          .catch((err) => {
            clearInterval(this.pollJob);
            if (res && res.data.logs) {
              this.jobMessages = res.data.logs;
            }
            this.loading.upgrading = false;
            this.jobProgress = 0;
            this.messages.push({
              message: `Failed copying model! ${err.response ? err.response.data.message : err.message}`,
              color: "error",
            });
            console.error(err);
            this.result = err.response.data;
          });
      }, 1000);
    },
    async cancelJob(jobId) {
      this.loading.cancelling = true;
      this.axios
        .get(`/contentful/cancel-job`, {
          params: {
            jobId: jobId,
          },
        })
        .then(async (res) => {
          if (res.data) {
            clearInterval(this.pollJob);
            this.loading.upgrading = false;
            this.messages.push({
              message: `Job cancelled successfully!`,
            });
          }
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed cancelling job! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        })
        .finally(() => (this.loading.cancelling = false));
    },
    async upgradeModel() {
      this.loading.upgrading = true;
      this.result = null;
      this.axios
        .post(`/contentful/upgrade-version`, {
          targets: this.selection.targets,
          releaseVersion: this.selection.modelVersion.version,
          backupTargetSpace: this.backupTargetSpace,
        })
        .then(async (res) => {
          this.jobId = res.data.jobId;
          await this.getJob(res.data.jobId, res);
        })
        .catch((err) => {
          clearInterval(this.pollJob);
          this.messages.push({
            message: `Failed upgrading new version! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
          this.loading.upgrading = false;
        });
    },
  },

  created() {
    this.getModelVersions();
  },
};
</script>

<style lang="scss" scoped>
.customer {
  position: relative;
}

.content_field_box {
  background-color: #7ecead61;
}

.inset {
  margin-left: 40px;
}
</style>
